import { Question, QuestionAnswer } from '@core/types';
import { Input } from '@core/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    question: Question;
    answer: QuestionAnswer | null;
    answerUpdated: (answer: QuestionAnswer) => void;
    classNames?: string;
}

const emptyGuid = '00000000-0000-0000-0000-000000000000';

export const QuestionnaireSingleChoiceQuestion = ({ question, answer, answerUpdated, classNames }: Props) => {
    const { t } = useTranslation();
    const [openAnswer, setOpenAnswer] = useState<string>('');
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);

    useEffect(() => {
        setOpenAnswer(answer?.openAnswer ?? '');
        if (answer?.selectedAnswers && answer.selectedAnswers.length > 0) {
            setSelectedAnswer(answer.selectedAnswers[0]);
        } else {
            setSelectedAnswer(null);
        }
    }, [answer]);

    useEffect(() => {
        if (!selectedAnswer) {
            answerUpdated({ questionId: question.questionId });
            return;
        }

        if (selectedAnswer === emptyGuid) {
            answerUpdated({ questionId: question.questionId, selectedAnswers: [emptyGuid], openAnswer: openAnswer });
        } else {
            answerUpdated({ questionId: question.questionId, selectedAnswers: [selectedAnswer], openAnswer: '' });
        }
    }, [selectedAnswer, openAnswer]);

    return (
        <>
            {question.options.map((option, index) => (
                <div key={index} className={`${classNames ? classNames : 'ps-6'}`}>
                    <input
                        type="radio"
                        name="answer"
                        id={`answer${index}`}
                        value={option.optionId}
                        checked={selectedAnswer === option.optionId}
                        onChange={(e) => setSelectedAnswer(option.optionId)}
                        className="size-1em appearance-none bg-inherit checked:bg-primary checked:bg-radio bg-center border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-start mt-0.25em -ms-6 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)]"
                    />
                    <label htmlFor={`answer${index}`} className="inline-block">
                        {option.text}
                    </label>
                </div>
            ))}
            {question.hasOtherOption && (
                <div className={`${classNames ? classNames : 'ps-6'}`}>
                    <input
                        type="radio"
                        name="answer"
                        id={`answer-other`}
                        value={-1}
                        checked={selectedAnswer === emptyGuid}
                        onChange={(e) => setSelectedAnswer(emptyGuid)}
                        className="size-1em appearance-none bg-inherit checked:bg-primary checked:bg-radio bg-center border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-start mt-0.25em -ms-6 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)]"
                    />
                    <label htmlFor={`answer-other`} className="inline-block">
                        {question.otherOptionText}
                    </label>
                    <Input
                        type="text"
                        name="other-value"
                        id={`other-value`}
                        value={openAnswer}
                        onChange={(e) => setOpenAnswer(e.target.value)}
                        placeholder={t('Questionnaire.OtherAnswer.Placeholder')}
                        classNames="mb-2"
                    />
                </div>
            )}
        </>
    );
};
